import { useQuery } from '@tanstack/react-query';
import type { AuditIssue, Bucket, Severity } from '@seo/types';
import { apiGet } from '@/lib/api';
import { isTerminal } from '@/lib/queryClient';

export interface AuditIssueFilters {
  severity?: Severity;
  bucket?: Bucket;
  check_id?: string;
  limit?: number;
}

export function useAuditIssues(id: string | undefined, filters: AuditIssueFilters = {}) {
  const params = new URLSearchParams();
  if (filters.severity) params.set('severity', filters.severity);
  if (filters.bucket) params.set('bucket', filters.bucket);
  if (filters.check_id) params.set('check_id', filters.check_id);
  if (filters.limit) params.set('limit', String(filters.limit));

  return useQuery({
    queryKey: ['audit-issues', id, params.toString()],
    queryFn: () =>
      apiGet<{ items: AuditIssue[]; has_more: boolean }>(
        `/v1/audits/${id}/issues?${params.toString()}`,
      ),
    enabled: !!id,
  });
}

/**
 * Single issue detail with sample affected URLs.
 */
export function useAuditIssue(auditId: string | undefined, issueId: string | undefined) {
  return useQuery({
    queryKey: ['audit-issue', auditId, issueId],
    queryFn: () =>
      apiGet<
        AuditIssue & {
          affected_urls_sample: Array<{ url: string; page_id: string | null; evidence: unknown }>;
        }
      >(`/v1/audits/${auditId}/issues/${issueId}`),
    enabled: !!auditId && !!issueId,
  });
}

/**
 * Paginated affected URLs for an issue.
 */
export function useAuditIssueUrls(
  auditId: string | undefined,
  issueId: string | undefined,
  limit = 100,
) {
  return useQuery({
    queryKey: ['audit-issue-urls', auditId, issueId, limit],
    queryFn: () =>
      apiGet<{
        items: Array<{ url: string; page_id: string | null; evidence: unknown }>;
        has_more: boolean;
      }>(`/v1/audits/${auditId}/issues/${issueId}/urls?limit=${limit}`),
    enabled: !!auditId && !!issueId,
  });
}

/**
 * v2 progress payload — polls while audit is non-terminal.
 */
export function useAuditProgress(auditId: string | undefined, status?: string) {
  return useQuery({
    queryKey: ['audit-progress', auditId],
    queryFn: () =>
      apiGet<{
        status: string;
        pages_discovered: number | null;
        pages_crawled: number | null;
        pages_failed: number | null;
        issues_count: number | null;
        health_score: number | null;
        progress: Record<string, unknown>;
      }>(`/v1/audits/${auditId}/progress`),
    enabled: !!auditId,
    refetchInterval: () => (isTerminal(status) ? false : 3000),
  });
}
